// Shared plan-feature resolver used by every place on the site that renders
// a plan's marketing bullet list (Landing, Pricing, Register, PlanPicker,
// Subscription page, in-app upgrade cards, etc.).
//
// Priority order:
//   1) plan.marketing_features  — free-text bullets edited in Ultra Admin
//                                 (Paket Düzenle > "Ana Sayfa Özellik Maddeleri").
//   2) plan.features            — technical feature-ID list (e.g. "advance_approval");
//                                 mapped to Turkish names through featureIdToName().
//   3) fallbackByPlanId[planId] — hard-coded marketing copy used only when the
//                                 plan has never been edited in Ultra Admin.
//
// A single utility guarantees the whole app stays in sync the moment an admin
// tweaks the bullets in Ultra Admin — no per-page code changes required.

/**
 * Resolve the human-readable marketing bullets for a plan.
 * @param {Object} plan Plan doc from backend (either camelCase or snake_case)
 * @param {Object} [opts]
 * @param {(id: string) => string} [opts.featureIdToName] Optional resolver that
 *   turns a technical feature ID into a Turkish label. If omitted, ID is used verbatim.
 * @param {Record<string,string[]>} [opts.fallbackByPlanId] Optional plan-id → bullets
 *   map used as the very last resort.
 * @returns {string[]}
 */
export function resolvePlanFeatures(plan, opts = {}) {
  if (!plan) return [];

  const marketing = Array.isArray(plan.marketing_features)
    ? plan.marketing_features.filter(Boolean)
    : Array.isArray(plan.marketingFeatures)
      ? plan.marketingFeatures.filter(Boolean)
      : [];
  if (marketing.length > 0) return marketing;

  const backendIds = Array.isArray(plan.features) ? plan.features : [];
  if (backendIds.length > 0) {
    const nameFn = typeof opts.featureIdToName === 'function' ? opts.featureIdToName : (x) => x;
    return backendIds.map((id) => nameFn(id) || id);
  }

  const planKey = plan.id || plan.slug || plan.code;
  const fallbackMap = opts.fallbackByPlanId || {};
  return fallbackMap[planKey] || [];
}
